import { escapeHtml } from 'common/filters/utils'
import type { CommonContextStore } from 'src/types/context'
import useContextStore from 'src/store/useContextStore'
import useURLStore from 'src/store/useURLStore'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import type { Rate } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/types'

type ContextWithSite = CommonContextStore['context'] & {
  site_pt?: {
    domain?: string
  }
}

/**
 * Ссылка с `&ZeroWidthSpace;` после тега:
 * иначе следующее слово визуально прилипает к анкору.
 */
function wrapLink(href: string, text: string) {
  return `<a href="${href}">${text}</a>&ZeroWidthSpace;`
}

/**
 * Подсветка тегов в тексте отзыва ссылками на услугу / препарат / манипуляцию.
 *
 * Серверные фильтры шаблонов нельзя тащить в клиентский бандл,
 * поэтому разметка собирается здесь.
 * При ошибке роутера или стора возвращается экранированный текст без ссылок,
 * чтобы карточка не падала.
 *
 * `hide_tags` у врача отключает подсветку целиком.
 */
export default function highlightReviewTags(
  text: string,
  rate: Rate,
  context: ContextWithSite,
): string {
  const escaped = escapeHtml(text)

  if (!escaped || rate.doctor?.hide_tags) {
    return escaped
  }

  const tags = rate.ratetag_set?.all || []

  if (!tags.length) {
    return escaped
  }

  try {
    const { reverseFull, reverseFullL10N } = useURLStore()
    const { urlLang }: CommonContextStore = useContextStore()
    const townTranslit = rate.doctor?.town?.translit
      || rate.lpu?.town?.translit
      || context.town.translit
    const sitePt = context.site_pt?.domain
    let output = escaped

    tags.forEach(tag => {
      if (!tag.active) {
        return
      }

      const tagText = tDynamic(tag, 'text') || tag.text || ''

      if (!tagText) {
        return
      }

      const checkSeparateWord = new RegExp(`(^|\\s)${tagText}($|\\s|,|\\.)`, 'gi')

      if (!output.match(checkSeparateWord)?.length) {
        return
      }

      let href = ''

      if (tag.drug?.translit && sitePt) {
        href = `https://${sitePt}/${tag.drug.translit}/`
      } else if (tag.manip?.translit) {
        href = reverseFullL10N({
          name: 'town_lechenie',
          args: {
            language: urlLang,
            town_translit: townTranslit,
            mtype_translit: tag.manip.translit,
          },
        })
      } else if (tag.dtype?.dclass?.group && tag.dtype.translit) {
        href = reverseFull({
          name: 'services_all',
          args: [townTranslit, tag.dtype.dclass.group, tag.dtype.translit],
        })
      } else if (tag.dclass?.group && tag.dclass.translit) {
        href = reverseFull({
          name: 'services_all',
          args: [townTranslit, tag.dclass.group, tag.dclass.translit],
        })
      } else if (tag.dcategory?.dclass?.group && tag.dcategory.dclass.translit && tag.dcategory.translit) {
        href = reverseFull({
          name: 'services_category',
          args: [
            townTranslit,
            tag.dcategory.dclass.group,
            tag.dcategory.dclass.translit,
            tag.dcategory.translit,
          ],
        })
      }

      if (href) {
        output = output.replace(tagText, wrapLink(href, tagText))
      }
    })

    return output
  } catch {
    return escaped
  }
}
